export type AnalyticsType = 'pricing' | 'conversation' | 'call'

export interface MetricDataPoint {
  phone_number: string
  account_name: string
  start: string
  end: string
  conversation?: number
  volume?: number
  count?: number
  cost: number
  average_duration?: number
}

export interface AggregatedMetric {
  phone_number: string
  account_name: string
  conversation: number
  volume: number
  count: number
  cost: number
  average_duration: number
}

export interface MetricsFilters {
  analyticsType: AnalyticsType
  startDate: string
  endDate: string
  phoneNumbers: { value: string, label: string }[]
}

export interface AnalyticsResponse {
  data_points: MetricDataPoint[]
}

export interface AnalyticsQueryParams {
  start?: string
  end?: string
  phone_numbers?: string
}

export interface AnalyticsReportParams extends AnalyticsQueryParams {
  report: true
}

export interface MetaAnalyticsClient {
  getConversationAnalytics: (wabaId: string, params: AnalyticsQueryParams) => Promise<AnalyticsResponse>
  getCallAnalytics: (wabaId: string, params: AnalyticsQueryParams) => Promise<AnalyticsResponse>
  getPricingAnalytics: (wabaId: string, params: AnalyticsQueryParams) => Promise<AnalyticsResponse>
  downloadConversationReport: (wabaId: string, params: AnalyticsReportParams) => Promise<Blob>
  downloadCallReport: (wabaId: string, params: AnalyticsReportParams) => Promise<Blob>
  downloadPricingReport: (wabaId: string, params: AnalyticsReportParams) => Promise<Blob>
}

interface AggregationAccumulator {
  phone_number: string
  account_name: string
  conversation: number
  volume: number
  count: number
  cost: number
  total_duration: number
}

export function filterMetrics (
  metrics: MetricDataPoint[],
  filters: MetricsFilters
): MetricDataPoint[] {
  return metrics.filter(metric => {
    const metricStart = new Date(metric.start)
    const metricEnd = new Date(metric.end)
    const filterStart = filters.startDate ? new Date(filters.startDate) : null
    const filterEnd = filters.endDate ? new Date(filters.endDate) : null
    const withinDateRange =
      (!filterStart || metricStart >= filterStart) &&
      (!filterEnd || metricEnd <= filterEnd)
    const matchesPhoneNumber =
      !filters.phoneNumbers.length ||
      filters.phoneNumbers.map(phone => phone.value).includes(metric.phone_number)
    return withinDateRange && matchesPhoneNumber
  })
}

export function aggregateMetrics (
  metrics: MetricDataPoint[],
  analyticsType: AnalyticsType
): AggregatedMetric[] {
  const aggregation: Record<string, AggregationAccumulator> = {}

  metrics.forEach(metric => {
    const phoneNumber = metric.phone_number
    if (!aggregation[phoneNumber]) {
      aggregation[phoneNumber] = {
        phone_number: phoneNumber,
        account_name: metric.account_name,
        conversation: 0,
        volume: 0,
        count: 0,
        cost: 0,
        total_duration: 0
      }
    }

    if (analyticsType === 'conversation') {
      aggregation[phoneNumber].conversation += metric.conversation ?? 0
    } else if (analyticsType === 'pricing') {
      aggregation[phoneNumber].volume += metric.volume ?? 0
    } else if (analyticsType === 'call') {
      const count = metric.count ?? 0
      const avgDuration = metric.average_duration ?? 0
      aggregation[phoneNumber].count += count
      aggregation[phoneNumber].total_duration += avgDuration * count
    }

    aggregation[phoneNumber].cost += metric.cost
  })

  return Object.values(aggregation).map(acc => ({
    phone_number: acc.phone_number,
    account_name: acc.account_name,
    conversation: acc.conversation,
    volume: acc.volume,
    count: acc.count,
    cost: acc.cost,
    average_duration: acc.count > 0 ? acc.total_duration / acc.count : 0
  }))
}

export function getReportFilename (analyticsType: AnalyticsType): string {
  if (analyticsType === 'conversation') return 'conversation_report.xlsx'
  if (analyticsType === 'call') return 'call_report.xlsx'
  return 'pricing_report.xlsx'
}

export function buildAnalyticsParams (
  filters: MetricsFilters,
  report = false
): AnalyticsQueryParams | AnalyticsReportParams {
  const params: AnalyticsQueryParams = {}
  if (filters.startDate && filters.endDate) {
    params.start = filters.startDate
    params.end = filters.endDate
  }
  if (filters.phoneNumbers.length > 0) {
    params.phone_numbers = filters.phoneNumbers.map(phone => phone.value).join(',')
  }
  if (report) {
    return { ...params, report: true }
  }
  return params
}

export async function fetchAnalytics (
  analyticsType: AnalyticsType,
  metaService: MetaAnalyticsClient,
  wabaId: string,
  params: AnalyticsQueryParams
): Promise<AnalyticsResponse> {
  if (analyticsType === 'conversation') {
    return metaService.getConversationAnalytics(wabaId, params)
  }
  if (analyticsType === 'call') {
    return metaService.getCallAnalytics(wabaId, params)
  }
  return metaService.getPricingAnalytics(wabaId, params)
}

export async function downloadAnalyticsReport (
  analyticsType: AnalyticsType,
  metaService: MetaAnalyticsClient,
  wabaId: string,
  params: AnalyticsReportParams
): Promise<Blob> {
  if (analyticsType === 'conversation') {
    return metaService.downloadConversationReport(wabaId, params)
  }
  if (analyticsType === 'call') {
    return metaService.downloadCallReport(wabaId, params)
  }
  return metaService.downloadPricingReport(wabaId, params)
}

export function computeTotalAverageDuration (metrics: AggregatedMetric[]): number {
  if (metrics.length === 0) return 0
  const totalCalls = metrics.reduce((acc, metric) => acc + metric.count, 0)
  if (totalCalls === 0) return 0
  const totalDuration = metrics.reduce((acc, metric) => acc + (metric.average_duration * metric.count), 0)
  return totalDuration / totalCalls
}

export function formatDuration (value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(value)
}
